import { DragHandle } from '@mui/icons-material';
import { Box, ListItem } from '@mui/material';
import { sortBy } from 'lodash';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DropResult,
  Droppable,
  DroppableProvided,
} from 'react-beautiful-dnd';
import { Control } from 'react-hook-form';

import { SectionDefinition } from '~/components/ReorderableSections/component';
import { QuickLinkEditFields } from '~/scenes/MyWorkspace/components/Widgets/components/QuickLinksWidget/components/ConfigureQuickLinksDialogContent/components/QuickLinkEditFields';
import { QuickLinksFieldArray } from '~/scenes/MyWorkspace/components/Widgets/components/QuickLinksWidget/components/ConfigureQuickLinksDialogContent/useConfigureQuickLinksDialogContent';
import { QuickLinksFormValues } from '~/scenes/MyWorkspace/components/Widgets/components/QuickLinksWidget/models';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';

import { useSxStyles } from './useStyles';

type Props = {
  control: Control<QuickLinksFormValues>;
  quickLinksFieldArray: QuickLinksFieldArray;
  workspaceType?: WorkspaceType;
};

export const QuickLinksOrderList = (props: Props) => {
  const {
    control,
    quickLinksFieldArray: { move, fields, remove },
    workspaceType,
  } = props;

  const isCompanyWorkspace = workspaceType === WorkspaceType.Company;

  const sxStyles = useSxStyles();

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) {
        return;
      }

      move(result.source.index, result.destination.index);
    },
    [move],
  );

  const { sectionDefinitions, sectionsOrder, droppableId } = useMemo(() => {
    const sectionDefinitions: SectionDefinition[] = fields.reduce(
      (definitions, field, index): SectionDefinition[] => {
        const fieldId = field?.id || '';

        if (!field) {
          return definitions;
        }

        return [
          ...definitions,
          {
            name: fieldId,
            content: (dragHandleProps?: React.HTMLAttributes<unknown>) => (
              <ListItem
                component="div"
                disableGutters={true}
                data-cy="list-item"
              >
                {!isCompanyWorkspace && (
                  <span {...dragHandleProps}>
                    <DragHandle
                      sx={sxStyles.sectionDragHandle}
                      data-cy="drag-handle"
                    />
                  </span>
                )}
                <QuickLinkEditFields
                  control={control}
                  index={index}
                  fieldItem={field}
                  remove={remove}
                  workspaceType={workspaceType}
                />
              </ListItem>
            ),
          },
        ];
      },
      [] as SectionDefinition[],
    );

    return {
      sectionDefinitions,
      droppableId: 'linksConfigure',
      sectionsOrder: fields.map((f) => f?.id).filter((f) => !!f),
    };
  }, [fields, remove, sxStyles, control, workspaceType, isCompanyWorkspace]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={droppableId}>
        {(droppableProvided: DroppableProvided) => {
          const orderedSections = sortBy(sectionDefinitions, ({ name }) =>
            sectionsOrder.indexOf(name),
          );

          const draggableSections = orderedSections.map((section, index) => {
            const draggableId = `${droppableId}:${section.name}`;

            return (
              <Draggable
                key={draggableId}
                draggableId={draggableId}
                index={index}
                isDragDisabled={isCompanyWorkspace}
              >
                {(draggableProvided: DraggableProvided) => (
                  <div
                    ref={draggableProvided.innerRef}
                    {...draggableProvided.draggableProps}
                    children={section.content(
                      draggableProvided.dragHandleProps ?? undefined,
                    )}
                  />
                )}
              </Draggable>
            );
          });

          return (
            <Box
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
              data-cy="list"
            >
              {draggableSections}
              {droppableProvided.placeholder}
            </Box>
          );
        }}
      </Droppable>
    </DragDropContext>
  );
};
